import { useContext, useEffect, useState } from "react";
import { TicketContext } from "../../../../Shared/contexts/TicketContext";
import TicketMeasure from "../../../../Measuring/TicketModal/TicketMeasure";
import { Button, Input, Popconfirm, Tabs } from "antd";
import { PlusCircleOutlined, ToolOutlined } from "@ant-design/icons";
import { api } from "../../../../../config";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../../../../interfaces/users";
import { toast } from "react-toastify";
import { EmptyState } from "../../../../Shared/Components/EmptyState/EmptyState";
import { hasMinimumPermissions } from "../../../../../utils/helpers/userHelpers";

export const MeasuringTab = () => {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const { ticket, refetch, onTicketUpdate } = useContext(TicketContext) as any;
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingBuilding, setLoadingBuilding] = useState(false);
  const [loadingMinhaVisita, setLoadingMinhaVisita] = useState(false);
  const [minhaVisitaClientName, setMinhaVisitaClientName] = useState("");

  const hasCreatePermission =
    hasMinimumPermissions("checklist", user) ||
    hasMinimumPermissions("measuring", user);
  const hasActiveMeasuringTicket = ticket.fieldReport?.some(
    (fieldReport: any) =>
      fieldReport.type === "MEASURING" &&
      fieldReport.status !== "MEASURING_CANCELLED"
  );
  const canCreateFieldReport =
    hasCreatePermission &&
    !hasActiveMeasuringTicket &&
    (ticket.status === 1 || ticket.buildingInProgress);

  useEffect(() => {
    setMinhaVisitaClientName(ticket.minhaVisitaClientName ?? "");
  }, [ticket.minhaVisitaClientName]);

  const createFieldReport = async () => {
    try {
      setLoadingCreate(true);
      await api(token).post(`ticket/checklist/${ticket.id}/field-report`, {});
      toast.success("Ticket de medição criado com sucesso");
      refetch?.();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error(
          "Este checklist já possui um ticket de medição ativo vinculado"
        );
        return;
      }
      toast.error("Ocorreu um erro ao criar o ticket de medição");
    } finally {
      setLoadingCreate(false);
    }
  };

  const toggleBuildingInProgress = async () => {
    try {
      setLoadingBuilding(true);
      await api(token).put(`ticket/checklist/${ticket.id}`, {
        buildingInProgress: !ticket.buildingInProgress,
      });
      onTicketUpdate?.(ticket.id, {
        buildingInProgress: !ticket.buildingInProgress,
      });
      toast.success("Ticket atualizado com sucesso");
      refetch?.();
    } catch (error: any) {
      toast.error("Ocorreu um erro ao atualizar o ticket");
    } finally {
      setLoadingBuilding(false);
    }
  };

  const saveMinhaVisitaClientName = async (value?: string) => {
    const nextName = value ?? minhaVisitaClientName;
    const normalizedName = nextName.trim() || null;

    try {
      setLoadingMinhaVisita(true);
      await api(token).put(`ticket/checklist/${ticket.id}`, {
        minhaVisitaClientName: normalizedName,
      });
      onTicketUpdate?.(ticket.id, {
        minhaVisitaClientName: normalizedName,
      });
      toast.success("Nome do Cliente no Minha Visita atualizado com sucesso");
      refetch?.();
    } catch (error: any) {
      toast.error("Ocorreu um erro ao atualizar o nome do Minha Visita");
    } finally {
      setLoadingMinhaVisita(false);
    }
  };

  return (
    <div className="w-full  overflow-y-scroll" style={{ height: "65vh" }}>
      <div className="flex justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-1">
          <Input
            value={minhaVisitaClientName}
            onChange={(event) => setMinhaVisitaClientName(event.target.value)}
            placeholder="Nome do Cliente no Minha Visita"
          />
          <Button
            type="primary"
            loading={loadingMinhaVisita}
            onClick={() => saveMinhaVisitaClientName()}
          >
            Salvar
          </Button>
          <Popconfirm
            title="Apagar nome?"
            description="O nome do Cliente no Minha Visita será removido."
            okText="Apagar"
            cancelText="Cancelar"
            onConfirm={() => saveMinhaVisitaClientName("")}
          >
            <Button danger loading={loadingMinhaVisita}>
              Apagar Nome
            </Button>
          </Popconfirm>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            size="small"
            danger={ticket.buildingInProgress}
            type={ticket.buildingInProgress ? "primary" : "default"}
            icon={<ToolOutlined />}
            loading={loadingBuilding}
            onClick={toggleBuildingInProgress}
          >
            {ticket.buildingInProgress ? "Remover Em Obras" : "Marcar Em Obras"}
          </Button>
          <Popconfirm
            title="Criar ticket de medição?"
            description="Um novo ticket de medição será vinculado a este checklist."
            okText="Criar"
            cancelText="Cancelar"
            onConfirm={createFieldReport}
            disabled={!canCreateFieldReport}
          >
            <Button
              size="small"
              shape="circle"
              type="primary"
              icon={<PlusCircleOutlined />}
              loading={loadingCreate}
              disabled={!canCreateFieldReport}
            />
          </Popconfirm>
        </div>
      </div>
      <Tabs
        tabPosition="left"
        items={
          ticket.fieldReport?.length
            ? ticket.fieldReport?.map((fieldReport: any) => ({
                label: fieldReport.id,
                key: fieldReport.id,
                children: (
                  <TicketMeasure
                    type="checklist"
                    disabled
                    ticket={{
                      ...fieldReport,
                      minhaVisitaClientName: ticket.minhaVisitaClientName,
                      ticket: {
                        ...(fieldReport.ticket ?? {}),
                        id: ticket.id,
                        minhaVisitaClientName: ticket.minhaVisitaClientName,
                      },
                    }}
                  />
                ),
              }))
            : [
                {
                  label: "Medição",
                  key: "empty",
                  children: <EmptyState />,
                },
              ]
        }
      />
    </div>
  );
};
